import { Injectable } from "@angular/core";
import {
	AffiliateService,
	CookieConsentConfigService,
	DataProtectionConfigService,
	FraudPreventionConfigService,
	GroupIBFraudPreventionService,
} from "@obg/ngx.common";
import {
	CoreHttpClient,
	DepositLimitPeriod,
	Jurisdiction,
	LoginRequest,
	LoginWithPersonalNumberRequest,
	Market,
	MarketingOffers,
	Message,
	MessageType,
	OperatingSystemService,
	PlatformName,
	ProductType,
} from "@obg/ngx.core";
import { PlatformService, StatusCodes } from "@obg/ngx.global";
import { castArray, isEmpty, map, replace } from "lodash-es";
import { Observable } from "rxjs";

import { RegistrationConfigService } from "./registration.config";
import { GENERIC_REGISTRATION_ERROR, REGISTER_MESSAGE_CODE } from "./registration.const";
import {
	AffiliateMetadata,
	BrRegistrationRequest,
	CPFRegistrationPayload,
	Credential,
	RegistrationCustomEvent,
	RegistrationFormFlow,
	RegistrationPayload,
	RegistrationRequest,
	RegistrationResponse,
	RegistrationStatusResponse,
	RegistrationVerificationResponse,
} from "./registration.model";

@Injectable({
	providedIn: "root",
})
export class RegistrationService {
	readonly isIOS: boolean;
	readonly isAndroid: boolean;

	constructor(
		private httpClient: CoreHttpClient,
		private config: RegistrationConfigService,
		private fraudPreventionConfigService: FraudPreventionConfigService,
		private groupIBFraudPreventionService: GroupIBFraudPreventionService,
		private affiliateService: AffiliateService,
		private registrationConfigService: RegistrationConfigService,
		private dataProtectionConfig: DataProtectionConfigService,
		private cookieConsentConfig: CookieConsentConfigService,
		private operatingSystemService: OperatingSystemService,
		private platformService: PlatformService
	) {
		this.isIOS = this.operatingSystemService.getOperatingSystem() === PlatformName.IOS;
		this.isAndroid = this.operatingSystemService.getOperatingSystem() === PlatformName.Android;
	}

	getAllTokens(): Observable<object> {
		return this.httpClient.get({
			url: this.config.getRegistrationTokensEndpoint(),
		});
	}

	getStatus(registrationToken: string): Observable<RegistrationStatusResponse> {
		return this.httpClient.get<RegistrationStatusResponse>({
			url: this.config.getRegistrationStatusEndpoint(registrationToken),
		});
	}

	getUsernameCheckStatus(username: string): Observable<object> {
		return this.httpClient.get({
			url: `${this.config.getRegistrationUsernameCheckEndpoint()}/${username}`,
			suppressErrorLogging: [StatusCodes.NotFound],
		});
	}

	register(payload: RegistrationPayload, jurisdiction: Jurisdiction, market: Market, product = ProductType.Common) {
		return this.httpClient.post<RegistrationResponse>({
			url: this.config.getRegisterEndpoint(payload.additionalDetails.country),
			data: this.buildRegisterRequest(market, payload, jurisdiction, product),
			headers:
				jurisdiction === Jurisdiction.Sga
					? {
							"X-OBG-EXPERIMENTS": "SGA2",
						}
					: undefined,
		});
	}

	registerCPFFlow(
		payload: CPFRegistrationPayload,
		jurisdiction: Jurisdiction,
		market: Market,
		product = ProductType.Common,
		flow: RegistrationFormFlow
	): Observable<RegistrationResponse> {
		const data =
			flow === RegistrationFormFlow.Simple
				? this.buildRegisterCPFSimpleRequest(market, payload, jurisdiction, product)
				: this.buildRegisterCPFComplexRequest(market, payload, jurisdiction, product);

		return this.httpClient.post<RegistrationResponse>({
			url: this.config.getRegisterEndpoint("BR"),
			data,
			headers: undefined,
		});
	}

	getAllVerifications(): Observable<object> {
		return this.httpClient.get({
			url: this.config.getRegistrationVerificationsEndpoint(),
		});
	}

	verifyToken(registrationToken: string): Observable<RegistrationVerificationResponse> {
		return this.httpClient.patch<RegistrationVerificationResponse>({
			url: this.config.getRegistrationTokenEndpoint(),
			data: {
				registrationToken,
			},
		});
	}

	buildLoginRequest(credential: Credential, iovationBlackBox?: string): LoginRequest {
		return {
			username: credential.email || credential.username,
			password: credential.password,
			iovationBlackBox: this.fraudPreventionConfigService.getEnabledInLogin() ? iovationBlackBox : "",
			fraudToken: this.groupIBFraudPreventionService.getSessionId(),
		};
	}

	buildLoginWithPersonalNumberRequest(
		credential: Credential,
		personalNumber: string,
		iovationBlackBox?: string
	): LoginWithPersonalNumberRequest {
		return {
			...this.buildLoginRequest(credential, iovationBlackBox),
			personalNumber,
		};
	}

	getErrors(registrationStatusResponse?: RegistrationStatusResponse, message?: Message) {
		if (message) {
			if (message.type === MessageType.Success) {
				return undefined;
			}

			const messageData = message.data && castArray(message.data);

			if (
				messageData &&
				Object.values(REGISTER_MESSAGE_CODE).find((errorCode) => errorCode === message.messageCode)
			) {
				return map(messageData, (messageDataItem) => messageDataItem.code.toLowerCase());
			}
			return [GENERIC_REGISTRATION_ERROR.toLowerCase()];
		}

		if (!registrationStatusResponse) {
			return undefined;
		}

		if (isEmpty(registrationStatusResponse.reasons)) {
			if (
				registrationStatusResponse.status === REGISTER_MESSAGE_CODE.completed ||
				registrationStatusResponse.status === REGISTER_MESSAGE_CODE.awaitingAccountVerification
			) {
				return undefined;
			}
			return [registrationStatusResponse.status];
		}

		return map(registrationStatusResponse.reasons, (reason) => reason.toLowerCase());
	}

	getAffiliateMetaData(): AffiliateMetadata[] {
		const bannerTag = this.affiliateService.getPartnerId();
		const affiliate = this.affiliateService.getCookieName();
		if (!bannerTag || !affiliate) {
			return [];
		}
		return [{ bannerTag, affiliate }];
	}

	getMarketingOffersEnabled(): boolean {
		return this.config.getMarketingOffersEnabled() && this.dataProtectionConfig.getMarketingConsentDisabled();
	}

	getDefaultMarketingOffers(): MarketingOffers {
		return this.config.getMarketingOffersEnabled() && !this.dataProtectionConfig.getMarketingConsentDisabled()
			? {
					acceptEmailOffers: false,
					acceptSmsOffers: false,
					acceptTelesalesOffers: false,
				}
			: this.config.getMarketingOffers();
	}

	getMarketingConsentsOptOutEnabled(country: string | undefined, jurisdiction: Jurisdiction): boolean {
		const optOutConfig = this.config.getMarketingConsentsOptOutCountries();
		if (country) {
			return optOutConfig.includes(country);
		}

		if (jurisdiction === Jurisdiction.Sga) {
			return optOutConfig.includes("SV");
		}

		return false;
	}

	truncateProvinceCodeFromCity(city: string): string {
		const lastCommaIndex = city.lastIndexOf(",");
		return (city = lastCommaIndex !== -1 ? city.slice(0, lastCommaIndex).trim() : city);
	}

	registrationDOMEvent(eventName: RegistrationCustomEvent, params?: Record<string, unknown>): void {
		if (!this.platformService.isBrowser) {
			return;
		}

		document.dispatchEvent(
			new CustomEvent<{ date: Date }>(eventName, {
				bubbles: true,
				detail: {
					date: new Date(),
					...params,
				},
			})
		);
	}

	private buildRegisterRequest(
		market: Market,
		payload: RegistrationPayload,
		jurisdiction: Jurisdiction,
		registeredFromProduct: ProductType
	): RegistrationRequest {
		const {
			additionalDetails,
			iovationBlackBox,
			legal,
			depositLimit,
			credential,
			gamingLimit,
			sessionLimit,
			complexDepositLimit,
			betLimit,
			wageringLimit,
			balanceLimit,
			document,
		} = payload;

		let { city } = additionalDetails;
		if (this.isCityWithProvinceCode(additionalDetails.country!)) {
			city = this.truncateProvinceCodeFromCity(additionalDetails.city!);
		}

		const {
			fiscalRegionCode,
			province,
			street,
			zipCode,
			country,
			currency,
			phoneExtension,
			phoneNumber,
			nationalIdNumber,
			houseNumber,
			state,
			additive,
			departmentOfResidence,
			residenceMunicipality,
			department,
			district,
		} = additionalDetails;

		let consents = payload.consents;
		let marketingOffers = payload.marketingOffers;

		if (!this.dataProtectionConfig.getMarketingConsentDisabled() && consents) {
			consents = {
				brandMarketing: this.getMarketingConsentsOptOutEnabled(payload.additionalDetails.country, jurisdiction)
					? !consents.brandMarketing
					: consents.brandMarketing,
				...(this.dataProtectionConfig.getGroupMarketingDisabled() && {
					groupMarketing: false,
					thirdParty: true,
				}),
				...(this.cookieConsentConfig.getOneTrustIsEnabled() && { oneTrust: consents.oneTrust }),
			};

			marketingOffers = {
				acceptEmailOffers: consents.brandMarketing,
				acceptSmsOffers: consents.brandMarketing,
				acceptTelesalesOffers: consents.brandMarketing,
			};
		}

		return {
			language: market.dataLanguageCode ?? market.languageCode,
			timeZone: "utc",
			address: {
				street,
				zipCode,
				city,
				country,
				fiscalRegionCode,
				province,
				houseNumber,
				state,
				additive,
				departmentOfResidence,
				residenceMunicipality,
				department,
				district,
			},
			affiliateMetadata: this.getAffiliateMetaData(),
			credential: {
				...credential,
				username: credential.username || credential.email,
			},
			currency,
			depositLimit:
				depositLimit?.depositLimitPeriod === DepositLimitPeriod.NoLimit ? undefined : payload.depositLimit,
			complexDepositLimit,
			subscriptions: {
				...marketingOffers,
				email: credential.email,
				phoneExtension: phoneExtension.replace(/(^\+?)/, "+"),
				phoneNumber,
			},
			person: {
				...payload.person,
				nationalIdNumber: nationalIdNumber && replace(nationalIdNumber, /-/g, ""),
				idType: payload.nationalDocument?.nationalDocumentType,
				idNumber: payload.nationalDocument?.nationalDocumentNumber,
			},
			iovationBlackBox: this.fraudPreventionConfigService.getEnabledInRegistration() ? iovationBlackBox : "",
			fraudToken: this.groupIBFraudPreventionService.getSessionId(),
			legal,
			consents,
			productMetadata: {
				registeredFromProduct,
			},
			gamingLimit,
			...(betLimit?.period !== 0 ? { betLimit } : {}),
			...(sessionLimit?.sessionLimitPeriod !== 0 ? { sessionLimit } : {}),
			...(wageringLimit?.period !== 0 ? { wageringLimit } : {}),
			balanceLimit,
			document,
			...(payload?.providerVerificationDetails && {
				providerVerificationDetails: payload?.providerVerificationDetails,
			}),
		};
	}

	private buildRegisterCPFSimpleRequest(
		market: Market,
		payload: CPFRegistrationPayload,
		jurisdiction: Jurisdiction,
		registeredFromProduct: ProductType
	): BrRegistrationRequest {
		const { iovationBlackBox, legal, credential } = payload;
		let consents = payload.consents;
		let marketingOffers = payload.marketingOffers;
		if (!this.dataProtectionConfig.getMarketingConsentDisabled() && consents) {
			consents = {
				brandMarketing: this.getMarketingConsentsOptOutEnabled(undefined, jurisdiction)
					? !consents.brandMarketing
					: consents.brandMarketing,
				...(this.dataProtectionConfig.getGroupMarketingDisabled() && {
					groupMarketing: false,
					thirdParty: true,
				}),
				...(this.cookieConsentConfig.getOneTrustIsEnabled() && { oneTrust: consents.oneTrust }),
			};

			marketingOffers = {
				acceptEmailOffers: consents.brandMarketing,
				acceptSmsOffers: consents.brandMarketing,
				acceptTelesalesOffers: consents.brandMarketing,
			};
		}

		return {
			language: market.dataLanguageCode ?? market.languageCode,
			timeZone: "utc",
			iovationBlackBox: this.fraudPreventionConfigService.getEnabledInRegistration() ? iovationBlackBox : "",
			fraudToken: this.groupIBFraudPreventionService.getSessionId(),
			person: {
				firstName: payload.person.firstName,
				lastName: payload.person.lastName,
				personalCodeType: payload.nationalDocument?.nationalDocumentType,
				personalCode: payload.nationalDocument?.nationalDocumentNumber,
			},
			legal,
			credential: {
				...credential,
				username: credential.username || credential.email,
			},
			currency: payload.additionalDetails?.currency || "BRL",
			subscriptions: {
				...marketingOffers,
				email: credential.email,
			},
			affiliateMetadata: this.getAffiliateMetaData(),
			consents,
			flow: RegistrationFormFlow.Simple,
			productMetadata: {
				registeredFromProduct,
			},
		};
	}

	private buildRegisterCPFComplexRequest(
		market: Market,
		payload: CPFRegistrationPayload,
		jurisdiction: Jurisdiction,
		registeredFromProduct: ProductType
	): BrRegistrationRequest {
		const simpleRequest = this.buildRegisterCPFSimpleRequest(market, payload, jurisdiction, registeredFromProduct);
		const {
			fiscalRegionCode,
			province,
			street,
			zipCode,
			city,
			country,
			currency,
			phoneExtension,
			phoneNumber,
			houseNumber,
			state,
			additive,
		} = payload.additionalDetails || {};

		return {
			...simpleRequest,
			currency: currency ?? "BRL",
			person: {
				...payload.person,
				personalCodeType: payload.nationalDocument?.nationalDocumentType,
				personalCode: payload.nationalDocument?.nationalDocumentNumber,
			},
			flow: RegistrationFormFlow.Complete,
			address: {
				street,
				zipCode,
				city,
				country,
				fiscalRegionCode,
				province,
				houseNumber,
				state,
				additive,
			},
			subscriptions: {
				...simpleRequest.subscriptions,
				phoneExtension: phoneExtension?.replace(/(^\+?)/, "+"),
				phoneNumber,
			},
			depositLimit:
				payload.depositLimit?.depositLimitPeriod === DepositLimitPeriod.NoLimit
					? undefined
					: payload.depositLimit,
		};
	}

	private isCityWithProvinceCode(countryCode: string): boolean {
		return this.registrationConfigService.getCityHasProvinceCode(countryCode);
	}
}
